import React, { useState, useEffect } from 'react';
import { X, Loader2, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PlatformConnectModal = ({ isOpen, onClose, brand }) => {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Ad account selection state
    const [step, setStep] = useState('platforms'); // 'platforms' | 'select-account'
    const [connectionId, setConnectionId] = useState(null);
    const [connectedPlatform, setConnectedPlatform] = useState(null);
    const [adAccounts, setAdAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [selectingAccount, setSelectingAccount] = useState(false);

    const platforms = [
        {
            id: 'meta',
            name: 'Meta Ads',
            description: 'Facebook & Instagram',
            icon: '📘',
            color: '#1877F2'
        },
        {
            id: 'google',
            name: 'Google Ads',
            description: 'Search & Display',
            icon: '🔍',
            color: '#4285F4'
        },
        {
            id: 'tiktok',
            name: 'TikTok Ads',
            description: 'Short-form video',
            icon: '🎵',
            color: '#000000'
        },
        {
            id: 'amazon',
            name: 'Amazon Ads',
            description: 'E-commerce advertising',
            icon: '📦',
            color: '#FF9900'
        }
    ];

    const handleConnect = async (platform) => {
        if (!currentUser || !brand) {
            setError('Missing user or brand information');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Request OAuth URL from backend
            const response = await fetch('http://localhost:3001/api/campaigns/connect-platform', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    platform: platform.id,
                    brandId: brand.id,
                    userId: currentUser.uid
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to initiate connection');
            }

            const { authUrl } = await response.json();

            // Open OAuth flow in popup
            const width = 600;
            const height = 700;
            const left = (window.screen.width / 2) - (width / 2);
            const top = (window.screen.height / 2) - (height / 2);

            const popup = window.open(
                authUrl,
                'oauth',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
            );

            // Listen for OAuth completion
            const handleMessage = async (event) => {
                if (event.data.type === 'oauth-success') {
                    console.log('OAuth successful:', event.data);
                    window.removeEventListener('message', handleMessage);

                    // Store connection info and move to account selection
                    setConnectionId(event.data.connectionId);
                    setConnectedPlatform(platform);
                    setLoading(false);

                    // Fetch ad accounts for selection
                    await fetchAdAccounts(event.data.connectionId, platform);

                } else if (event.data.type === 'oauth-error') {
                    console.error('OAuth error:', event.data.error);
                    setError(event.data.error);
                    setLoading(false);
                    window.removeEventListener('message', handleMessage);
                }
            };

            window.addEventListener('message', handleMessage);

            // Check if popup was blocked
            if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                throw new Error('Popup was blocked. Please allow popups for this site.');
            }

            // Monitor popup closure (user cancelled)
            // Wrap in try-catch to handle Cross-Origin-Opener-Policy restrictions
            const checkClosed = setInterval(() => {
                try {
                    if (popup.closed) {
                        clearInterval(checkClosed);
                        window.removeEventListener('message', handleMessage);
                        setLoading(false);
                    }
                } catch (e) {
                    // COOP policy blocks access to popup.closed
                    // Clear interval and rely on message events instead
                    clearInterval(checkClosed);
                }
            }, 500);

        } catch (err) {
            console.error('Connection error:', err);
            setError(err.message);
            setLoading(false);
        }
    };

    const fetchAdAccounts = async (connId, platform) => {
        setLoadingAccounts(true);
        setStep('select-account');
        setError(null);

        try {
            const response = await fetch(
                `http://localhost:3001/api/campaigns/ad-accounts?connectionId=${connId}&userId=${currentUser.uid}`
            );
            const data = await response.json();

            if (!response.ok) {
                const errorMsg = data.details || data.error || 'Failed to fetch ad accounts';
                throw new Error(errorMsg);
            }

            setAdAccounts(data.adAccounts || []);

            // If only one account, auto-select it
            if (data.adAccounts && data.adAccounts.length === 1) {
                await handleSelectAccount(data.adAccounts[0], connId);
            }
        } catch (err) {
            console.error('Error fetching ad accounts:', err);
            setError(err.message);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const handleSelectAccount = async (account, connId = connectionId) => {
        setSelectingAccount(true);
        setError(null);

        try {
            const response = await fetch('http://localhost:3001/api/campaigns/select-ad-account', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    connectionId: connId,
                    userId: currentUser.uid,
                    accountId: account.id,
                    accountName: account.name
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to select ad account');
            }

            // Success! Close modal and trigger refresh
            onClose(true);

        } catch (err) {
            console.error('Error selecting ad account:', err);
            setError(err.message);
        } finally {
            setSelectingAccount(false);
        }
    };

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setError(null);
            setLoading(false);
            setStep('platforms');
            setConnectionId(null);
            setConnectedPlatform(null);
            setAdAccounts([]);
            setLoadingAccounts(false);
            setSelectingAccount(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(4px)'
            }}
            onClick={() => step === 'platforms' && onClose()}
        >
            <div
                className="card"
                style={{
                    maxWidth: '600px',
                    width: '90%',
                    maxHeight: '80vh',
                    overflow: 'auto',
                    position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {step === 'platforms' ? (
                    <>
                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 'var(--space-6)'
                        }}>
                            <div>
                                <h2 style={{ margin: 0, marginBottom: 'var(--space-2)' }}>
                                    Connect Ad Platform
                                </h2>
                                <p className="text-secondary" style={{ margin: 0 }}>
                                    Connect your advertising accounts to monitor campaign performance
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="btn btn-ghost"
                                style={{ padding: 'var(--space-2)' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div
                                style={{
                                    background: 'var(--color-error-bg)',
                                    color: 'var(--color-error)',
                                    padding: 'var(--space-4)',
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: 'var(--space-4)',
                                    border: '1px solid var(--color-error)'
                                }}
                            >
                                {error}
                            </div>
                        )}

                        {/* Platform Cards Grid */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 'var(--space-4)'
                            }}
                        >
                            {platforms.map((platform) => (
                                <div
                                    key={platform.id}
                                    className="card"
                                    style={{
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        opacity: loading ? 0.6 : 1,
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        border: `2px solid var(--color-border)`,
                                        background: 'var(--color-bg-secondary)'
                                    }}
                                    onClick={() => !loading && handleConnect(platform)}
                                    onMouseEnter={(e) => {
                                        if (!loading) {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                                            e.currentTarget.style.borderColor = platform.color;
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                                        e.currentTarget.style.borderColor = 'var(--color-border)';
                                    }}
                                >
                                    <div style={{
                                        fontSize: 'var(--font-size-4xl)',
                                        marginBottom: 'var(--space-3)',
                                        textAlign: 'center'
                                    }}>
                                        {platform.icon}
                                    </div>

                                    <h3 style={{
                                        margin: 0,
                                        marginBottom: 'var(--space-1)',
                                        fontSize: 'var(--font-size-lg)',
                                        textAlign: 'center'
                                    }}>
                                        {platform.name}
                                    </h3>

                                    <p className="text-secondary" style={{
                                        margin: 0,
                                        fontSize: 'var(--font-size-sm)',
                                        textAlign: 'center'
                                    }}>
                                        {platform.description}
                                    </p>

                                    <button
                                        className="btn btn-primary"
                                        style={{
                                            width: '100%',
                                            marginTop: 'var(--space-4)',
                                            background: platform.color
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? 'Connecting...' : 'Connect'}
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Info Message */}
                        <div
                            style={{
                                marginTop: 'var(--space-6)',
                                padding: 'var(--space-4)',
                                background: 'var(--color-bg-tertiary)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border)'
                            }}
                        >
                            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                Your credentials are encrypted and securely stored. We only request read-only access to your campaign data.
                            </p>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Ad Account Selection Step */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 'var(--space-6)'
                        }}>
                            <div>
                                <h2 style={{ margin: 0, marginBottom: 'var(--space-2)' }}>
                                    Select Ad Account
                                </h2>
                                <p className="text-secondary" style={{ margin: 0 }}>
                                    Choose which {connectedPlatform?.name} account to connect to {brand?.name}
                                </p>
                            </div>
                            <div style={{
                                fontSize: 'var(--font-size-3xl)'
                            }}>
                                {connectedPlatform?.icon}
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    background: 'var(--color-error-bg)',
                                    color: 'var(--color-error)',
                                    padding: 'var(--space-4)',
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: 'var(--space-4)',
                                    border: '1px solid var(--color-error)'
                                }}
                            >
                                <AlertCircle size={18} />
                                {error}
                            </div>
                        )}

                        {loadingAccounts ? (
                            <div style={{
                                textAlign: 'center',
                                padding: 'var(--space-8)'
                            }}>
                                <Loader2 size={32} className="spinning" style={{ margin: '0 auto var(--space-4)' }} />
                                <p className="text-secondary">Loading your ad accounts...</p>
                            </div>
                        ) : adAccounts.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: 'var(--space-8)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                <AlertCircle size={48} style={{ margin: '0 auto var(--space-4)', opacity: 0.5 }} />
                                <h3>No Ad Accounts Found</h3>
                                <p>We couldn't find any ad accounts associated with your {connectedPlatform?.name} login.</p>
                                <button
                                    className="btn btn-secondary"
                                    style={{ marginTop: 'var(--space-4)' }}
                                    onClick={() => {
                                        setStep('platforms');
                                        setError(null);
                                    }}
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                {adAccounts.map(account => (
                                    <button
                                        key={account.id}
                                        onClick={() => handleSelectAccount(account)}
                                        disabled={selectingAccount}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            padding: 'var(--space-4)',
                                            background: 'var(--color-bg-secondary)',
                                            border: '2px solid var(--color-border)',
                                            borderRadius: 'var(--radius-lg)',
                                            cursor: selectingAccount ? 'wait' : 'pointer',
                                            textAlign: 'left',
                                            opacity: selectingAccount ? 0.6 : 1,
                                            transition: 'border-color 0.2s, background 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!selectingAccount) {
                                                e.currentTarget.style.borderColor = connectedPlatform?.color || 'var(--color-primary)';
                                                e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--color-border)';
                                            e.currentTarget.style.background = 'var(--color-bg-secondary)';
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}>
                                                {account.name || `Ad Account ${account.id}`}
                                            </div>
                                            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                                Account ID: {account.id}
                                                {account.currency && ` • ${account.currency}`}
                                                {account.timezone && ` • ${account.timezone}`}
                                            </div>
                                        </div>
                                        {selectingAccount ? (
                                            <Loader2 size={20} className="spinning" />
                                        ) : (
                                            <div style={{
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                border: '2px solid var(--color-border)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Info about permanent selection */}
                        {adAccounts.length > 0 && (
                            <div
                                style={{
                                    marginTop: 'var(--space-6)',
                                    padding: 'var(--space-4)',
                                    background: 'var(--color-bg-tertiary)',
                                    borderRadius: 'var(--radius-md)',
                                    border: '1px solid var(--color-border)'
                                }}
                            >
                                <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                    This account will be permanently linked to <strong>{brand?.name}</strong>. All campaigns from this account will sync to this brand.
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PlatformConnectModal;
